import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import {
  QuestionSource,
  QuestionSourceAnswer,
} from '../../../../core/interfaces/question';
import { MathTextComponent } from '../../../../shared/math/math-text.component';

/** Un champ de référentiel, confronté à ce que le document disait. */
interface FieldDiff {
  readonly labelKey: string;
  readonly before: string;
}

/**
 * D'où vient une question importée, et ce que le document disait.
 *
 * <p>La trace existait en base depuis que l'écran de contrôle permet de
 * corriger une question avant l'import — <code>source_ref.edited</code> — mais
 * aucun écran ne la lisait. Une traçabilité que personne ne peut consulter ne
 * répond à rien.</p>
 *
 * <p>Le panneau ne montre le bloc de correction que si le document a été
 * corrigé. Son absence est une information : la question est conforme à sa
 * ligne.</p>
 *
 * <p><b>Ce qu'il montre, c'est le document</b> — pas la question, qui est déjà
 * à l'écran juste en dessous. Comparer les deux est le geste que cet écran
 * rend possible.</p>
 */
@Component({
  selector: 'app-question-source',
  standalone: true,
  imports: [MathTextComponent, CommonModule, TranslateModule],
  templateUrl: './question-source.component.html',
  styleUrl: './question-source.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionSourceComponent {
  readonly source = input<QuestionSource | null>(null);

  /** Rien à montrer pour une question saisie à la main. */
  readonly hasSource = computed(() => {
    const s = this.source();
    return !!s && (!!s.fileName || s.row != null || !!s.edited);
  });

  readonly edited = computed(() => this.source()?.edited ?? null);

  readonly answers = computed<readonly QuestionSourceAnswer[]>(
    () => this.edited()?.original.answers ?? [],
  );

  /**
   * Les champs de référentiel que le document portait, vides exclus.
   *
   * <p>Afficher « Niveau : — » sur cinq lignes noierait les deux qui ont
   * quelque chose à dire.</p>
   */
  readonly fields = computed<readonly FieldDiff[]>(() => {
    const o = this.edited()?.original;
    if (!o) return [];

    const candidats: readonly (readonly [string, string | null | undefined])[] = [
      ['TEST_IMPORT.ROLE.THEME', o.theme],
      ['TEST_IMPORT.ROLE.LEVEL', o.level],
      ['TEST_IMPORT.ROLE.SKILL', o.skill],
      ['TEST_IMPORT.ROLE.TARGETED', o.targeted],
      ['TEST_IMPORT.ROLE.PREREQUISITES', o.prerequisites],
    ];

    return candidats
      .filter(([, value]) => !!value?.trim())
      .map(([labelKey, value]) => ({ labelKey, before: value!.trim() }));
  });
}
